// Loupe-style preview: a square 1:1 crop centred on the iris with a
// 1×–4× zoom slider. Used in the Studio when mum toggles "Loupe mode".
//
// Visual model:
//   - Outer container is square (matches the eye's aspect ratio 1:1).
//   - Inner `<img>` is scaled with `transform: scale(zoom)` from the
//     centre, and overflow is clipped on the container.
//   - The zoom slider sits below the image; the value is local state
//     owned by this component (parent doesn't need to know).

import { useEffect, useRef, useState } from 'react';
import { Slider } from './Slider';

export interface LoupePreviewProps {
  imageUrl: string;
  alt: string;
}

const ZOOM_MIN = 1;
const ZOOM_MAX = 4;
const ZOOM_STEP = 0.1;

export function LoupePreview(props: LoupePreviewProps) {
  const { imageUrl, alt } = props;
  const [zoom, setZoom] = useState<number>(1);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // When the image changes (mum generated a new eye), reset zoom.
  useEffect(() => {
    setZoom(1);
  }, [imageUrl]);

  return (
    <div className="flex flex-col items-center gap-3">
      <div
        ref={containerRef}
        className="relative aspect-square w-full max-w-[480px] overflow-hidden rounded-lg border border-neutral-300 bg-neutral-900 dark:border-neutral-700"
      >
        <img
          src={imageUrl}
          alt={alt}
          draggable={false}
          className="absolute left-0 top-0 h-full w-full select-none object-cover"
          style={{
            transform: `scale(${zoom})`,
            transformOrigin: 'center center',
            transition: 'transform 120ms ease-out',
          }}
        />
        <span
          aria-hidden
          className="pointer-events-none absolute right-2 top-2 rounded-md bg-black/50 px-2 py-1 text-xs text-white"
        >
          {zoom.toFixed(1)}×
        </span>
      </div>
      <div className="w-full max-w-[480px]">
        <Slider
          label="Zoom"
          value={zoom}
          min={ZOOM_MIN}
          max={ZOOM_MAX}
          step={ZOOM_STEP}
          onChange={setZoom}
          description="Magnify the iris detail. 1× shows the full square crop."
        />
      </div>
    </div>
  );
}
